import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Loader2, MoreHorizontal, Sparkles, X, Files,
} from 'lucide-react';
import { apiService } from '../services/api';
import { toast } from 'sonner';
import { TurnRow, TransientCommand } from './chat/TurnRow';
import { ComposerBar } from './chat/ComposerBar';
import { ZeroCreditsBanner } from './chat/ZeroCreditsBanner';
import { FilePreviewModal } from './chat/FilePreviewModal';
// BuildTheatre removed in v1.7 (product decision) — the mock phone was replaced by the ENGINE RUNNING timeline.
import WorkspaceContextChips from './WorkspaceContextChips';
import type {
  Conversation, UICard, Turn,
} from './chat/types';
import {
  ACTIVE_STATES, WAITING_USER_STATES, STATUS_PILL_TEXT, statusPillText,
} from './chat/types';

import type { WorkspaceKind } from '../config/workspaceKinds';
import { useT } from '../contexts/LocaleContext';

interface Props {
  workspaceId: string;
  workspaceName?: string;
  workspaceKind?: WorkspaceKind;
  onOpenDrawer?: (section: 'files' | 'deploy' | 'memory' | 'config' | 'observability') => void;
}

export const WorkspaceChatPanel: React.FC<Props> = ({ workspaceId, workspaceName, workspaceKind, onOpenDrawer }) => {
  const t = useT();
  const [conv, setConv] = useState<Conversation | null>(null);
  const [previewPath, setPreviewPath] = useState<string | null>(null);
  const [previewBaseline, setPreviewBaseline] = useState<string | null>(null);
  const [showModifiedFiles, setShowModifiedFiles] = useState<null | string[]>(null);
  const [showSummary, setShowSummary] = useState<null | string>(null);
  const [deployModalOpen, setDeployModalOpen] = useState(false);
  const [insertSignal, setInsertSignal] = useState<{ text: string; nonce: number } | null>(null);

  const pushPromptToComposer = useCallback((prompt: string) => {
    setInsertSignal({ text: prompt, nonce: Date.now() });
  }, []);
  const [deployBusy, setDeployBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const lastTurnIdRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await apiService.getWorkspaceConversation(workspaceId);
      setConv(data);
    } catch (_e) { /* silent */ }
  }, [workspaceId]);

  useEffect(() => { load(); }, [load]);

  // Feb 2026 redesign: consume any pending `seed_intent` set by the
  // new Home screen. Fire it EXACTLY ONCE the first time we see a
  // conversation with zero user turns. This is what makes "prompt →
  // workspace → agente trabajando" feel instant, with no extra step.
  const seedFiredRef = useRef(false);
  useEffect(() => {
    if (!conv || seedFiredRef.current) return;
    const hasUserTurn = conv.turns?.some((t: any) => t.role === 'user');
    if (hasUserTurn) { seedFiredRef.current = true; return; }
    // Read the workspace metadata for the seed_intent
    (async () => {
      try {
        const ws = await apiService.getWorkspace(workspaceId);
        const seed = ws?.config?.custom_config?.seed_intent;
        if (seed && !seedFiredRef.current) {
          seedFiredRef.current = true;
          await handleSend(String(seed), []);
          // Clear the seed so re-mounts don't re-fire it
          try {
            await apiService.updateWorkspace?.(workspaceId, {
              config: {
                ...(ws.config || {}),
                custom_config: {
                  ...((ws.config || {}).custom_config || {}),
                  seed_intent: null,
                },
              },
            });
          } catch { /* best effort */ }
        }
      } catch { /* no-op */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv?.id, workspaceId]);

  useEffect(() => {
    if (!conv) return;
    const active = ACTIVE_STATES.has(conv.engine_state);
    const interval = active ? 1200 : 5000;
    const t = setInterval(load, interval);
    return () => clearInterval(t);
  }, [conv, load]);

  useEffect(() => {
    if (!conv) return;
    const last = conv.turns[conv.turns.length - 1];
    if (last && last.id !== lastTurnIdRef.current) {
      lastTurnIdRef.current = last.id;
      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({
          top: scrollRef.current.scrollHeight, behavior: 'smooth',
        });
      });
    }
  }, [conv?.turns?.length, conv]);

  const runSlash = async (cmd: string) => {
    try {
      if (cmd === '/clear') {
        if (!window.confirm('¿Archivar la conversación y empezar nueva?')) return;
        const data = await apiService.clearWorkspaceConversation(workspaceId);
        setConv(data); toast.success('Conversación archivada.');
      } else if (cmd === '/pause') {
        const data = await apiService.pauseWorkspace(workspaceId);
        setConv(data); toast.message('Pausa solicitada.');
      } else if (cmd === '/resume') {
        const data = await apiService.resumeWorkspace(workspaceId, '(continuar)');
        setConv(data);
      } else if (cmd === '/export') {
        await doExport();
      } else if (cmd === '/repo') {
        const cache = conv?.context_cache?.repo;
        if (cache) toast.message(`Repo: ${cache.paths_count} archivos · cache ${cache.fingerprint?.slice(0, 18)}`);
        else toast.message('Aún no he indexado el repo en esta sesión.');
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'No se pudo ejecutar el comando');
    }
  };

  const doExport = async () => {
    const data = await apiService.exportWorkspaceConversation(workspaceId);
    const blob = new Blob([data.content], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = data.filename; a.click();
    URL.revokeObjectURL(url);
    toast.success('Conversación exportada.');
  };

  const handleSend = async (text: string, attachments: any[]) => {
    if (!conv) return;
    const cleanAttachments = attachments
      .filter((a) => a.uri && !a.uploading && !a.error)
      .map((a) => ({
        kind: a.mime?.startsWith('image/') ? 'image' : 'file',
        name: a.name, uri: a.uri, mime: a.mime, size: a.size,
      }));
    try {
      const isWaiting = WAITING_USER_STATES.has(conv.engine_state);
      if (isWaiting) {
        // Legacy pause/resume flow (Builder está esperando input).
        const data = await apiService.resumeWorkspace(workspaceId, text, cleanAttachments);
        setConv(data);
        return;
      }
      // Sprint Cierre Final v1.27: TODO mensaje conversacional dentro
      // del workspace pasa por el Arquitecto (Coordinator + Tools).
      // El Arquitecto decide si conversar, preguntar 1-2 cosas, o
      // disparar el Builder. Nunca sugiere comandos legacy.
      const archAttachments = attachments
        .filter((a) => a.uri && !a.uploading && !a.error)
        .map((a) => ({
          kind: a.mime?.startsWith('image/') ? 'image' : 'file',
          name: a.name,
          data_url: a.mime?.startsWith('image/') ? a.uri : undefined,
          url: !a.mime?.startsWith('image/') ? a.uri : undefined,
        }));
      await apiService.architectChat({
        message: text,
        workspace_id: workspaceId,
        attachments: archAttachments,
      });
      // Rehidratar la conversación para mostrar el nuevo turno.
      const data = await apiService.getWorkspaceConversation(workspaceId);
      setConv(data);
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Error al enviar');
    }
  };

  const handlePause = async () => {
    try {
      const data = await apiService.pauseWorkspace(workspaceId);
      setConv(data);
      toast.message('Pausa solicitada. La acción actual termina y se detiene.');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'No se pudo pausar');
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('¿Cancelar el trabajo en curso? El motor se detendrá.')) return;
    try {
      const data = await apiService.cancelWorkspace(workspaceId);
      setConv(data);
      toast.message('Trabajo cancelado.');
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'No se pudo cancelar');
    }
  };

  const uploadFile = async (f: File) => {
    return await apiService.uploadWorkspaceAttachment(workspaceId, f);
  };

  const onOpenFile = (path: string, baseline?: string) => {
    setPreviewPath(path);
    setPreviewBaseline(baseline || null);
  };

  // Find the latest result turn (for action handlers)
  const latestResult = useMemo(() => {
    if (!conv) return null;
    for (let i = conv.turns.length - 1; i >= 0; i--) {
      if (conv.turns[i].kind === 'result' && conv.turns[i].role === 'engine') {
        return conv.turns[i];
      }
    }
    return null;
  }, [conv]);

  const turnHandlers = useMemo(() => ({
    onPreview: () => {
      // The Preview button should open the LIVE app so the user can
      // navigate the generated interface. We compose the same
      // token-bearing URL as SummaryCard's inline card and open it
      // in a new tab.
      //
      // v24.2 · CACHE-BUSTING: append `t=<preview_bust or Date.now()>`
      // so the browser never serves a stale copy on the new-tab open.
      // Root cause of the "resumen dice hecho pero Preview muestra la
      // versión anterior": we were opening the same URL twice; the
      // browser's HTTP cache (or the runtime proxy's) served the old
      // response. Adding a fresh query param makes the URL unique per
      // click and guarantees a network fetch.
      const detail = latestResult?.detail || {};
      const previewPath = detail.preview_url as string | undefined;
      const previewBust = (detail.preview_bust as number | undefined) || Date.now();
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const tk = localStorage.getItem('nexus_token') || '';
      const path = previewPath
        ? (previewPath.startsWith('/') ? previewPath : `/${previewPath}`).replace(/\/$/, '')
        : `/api/workspaces/${workspaceId}/runtime/proxy`;
      const parts: string[] = [];
      if (tk) parts.push(`token=${encodeURIComponent(tk)}`);
      parts.push(`t=${previewBust}`);
      const url = `${base}${path}/?${parts.join('&')}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    onExport: doExport,
    onShowModifiedFiles: () => {
      const patches = latestResult?.detail?.report?.patches || [];
      setShowModifiedFiles(patches.map((p: any) => p.path));
    },
    onRegression: async () => {
      toast.message('Lanzando regresión completa…');
      await handleSend('Ejecuta la regresión completa sobre los cambios aplicados.', []);
    },
    onRollback: async () => {
      if (!window.confirm('¿Revertir todos los parches aplicados en este resultado?')) return;
      await handleSend('Revierte los parches aplicados en el último resultado (rollback).', []);
    },
    onShowSummary: () => {
      const r = latestResult?.detail?.report;
      const text = r ? JSON.stringify(r, null, 2) : 'Sin reporte disponible.';
      setShowSummary(text);
    },
    onDeploy: () => {
      // Prefer opening the workspace drawer's Deploy section — it hosts
      // the full DeployPanel with GitHub connect flow, branch selector
      // and history. Fallback to the compact modal if no drawer.
      if (onOpenDrawer) onOpenDrawer('deploy');
      else setDeployModalOpen(true);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [latestResult]);

  // Transient command filter
  const displayedTurns = useMemo(() => {
    if (!conv) return [] as Turn[];
    const out: Turn[] = [];
    for (let i = 0; i < conv.turns.length; i++) {
      const t = conv.turns[i];
      const card = t.detail?.card;
      const isTransient = card?.transient;
      if (isTransient) {
        const later = conv.turns.slice(i + 1);
        const hasLater = later.some((lt) => !lt.detail?.card?.transient);
        if (hasLater || !ACTIVE_STATES.has(conv.engine_state)) continue;
      }
      out.push(t);
    }
    return out;
  }, [conv]);

  const activeTransient = useMemo(() => {
    if (!conv || !ACTIVE_STATES.has(conv.engine_state)) return null;
    for (let i = conv.turns.length - 1; i >= 0; i--) {
      const c = conv.turns[i].detail?.card;
      if (c?.transient) return c as UICard;
    }
    return null;
  }, [conv]);

  const statusPill = useMemo(() => {
    if (!conv) return null;
    const s = conv.engine_state;
    if (s === 'engine_idle' || s === 'engine_waiting') return null;
    return statusPillText(t, s) || STATUS_PILL_TEXT[s] || null;
  }, [conv, t]);

  const isActive = ACTIVE_STATES.has(conv?.engine_state || '');
  const isWaiting = WAITING_USER_STATES.has(conv?.engine_state || '');

  return (
    <div
      className="flex flex-col nx-glass-strong rounded-2xl overflow-hidden chat-full-height"
      style={{ ['--chat-offset' as any]: '220px' }}
      data-testid="workspace-chat-panel"
    >
      <div className="px-4 sm:px-6 py-3 flex items-center justify-between gap-3 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-violet-700 grid place-items-center text-white font-semibold shrink-0 shadow-lg shadow-violet-900/30">
            N
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium text-zinc-100 truncate">
              {workspaceName || 'Taller'}
            </div>
            <div className="text-[11px] text-zinc-500 truncate">
              {conv?.turns.length || 0} mensajes
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => runSlash('/clear')}
          className="text-zinc-500 hover:text-zinc-200 p-2 rounded-full hover:bg-white/[0.06] transition-colors"
          data-testid="chat-clear-btn"
          title="Nueva conversación"
        >
          <MoreHorizontal className="w-5 h-5" />
        </button>
      </div>

      <div className="h-px bg-white/[0.04] mx-4 sm:mx-6 shrink-0" />

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 py-5 space-y-4"
        data-testid="chat-transcript"
        style={{ WebkitOverflowScrolling: 'touch' as any }}
      >
        {conv && conv.turns.length === 0 && (
          <div className="text-center text-zinc-500 mt-12 sm:mt-16 px-4">
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-violet-500/15 to-cyan-500/10 border border-violet-400/20 grid place-items-center">
              <Sparkles className="w-6 h-6 text-violet-300" />
            </div>
            <p className="text-sm text-zinc-300">Estás dentro del proyecto.</p>
            <p className="text-xs mt-2 leading-relaxed">
              Cuéntame en qué estás pensando. Podemos empezar por lo que
              quieras: una idea, una duda, algo que te gustaría mejorar,
              o simplemente conversar sobre el proyecto.
            </p>
            <p className="text-[11px] mt-3 leading-relaxed text-zinc-600">
              Yo me encargo del resto — cuando algo esté listo para
              construir, lo preparo y avanzamos.
            </p>
          </div>
        )}

        <AnimatePresence initial={false}>
          {displayedTurns.map((t) => (
            <TurnRow
              key={t.id} turn={t} workspaceId={workspaceId}
              onOpenFile={onOpenFile}
              {...turnHandlers}
            />
          ))}
        </AnimatePresence>

        {/* v1.7 · Build Theatre eliminado por decisión de producto —
             el teléfono/preview simulado no aportaba valor real y el
             ENGINE RUNNING timeline en TurnRow cubre la comunicación
             de progreso durante la generación. */}
      </div>

      <AnimatePresence>
        {activeTransient && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="px-4 sm:px-6 pb-2 shrink-0"
          >
            <TransientCommand card={activeTransient} />
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {statusPill && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="px-4 sm:px-6 pb-1 shrink-0"
          >
            <div className="inline-flex items-center gap-2 text-xs text-zinc-400">
              <Loader2 className="w-3 h-3 animate-spin text-violet-300" />
              <span>{statusPill}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <ZeroCreditsBanner
        returnTo={typeof window !== 'undefined' ? window.location.pathname + window.location.search : undefined}
      />

      <ComposerBar
        disabled={false}
        isWaitingUser={isWaiting}
        isActive={isActive}
        onSend={handleSend}
        onPause={handlePause}
        onCancel={handleCancel}
        onSlash={runSlash}
        uploadFile={uploadFile}
        insertSignal={insertSignal}
      />

      {workspaceKind && workspaceKind.quickPrompts.length > 0 && (
        <div className="px-3 pb-2 -mt-1">
          <WorkspaceContextChips
            kind={workspaceKind}
            onInsertPrompt={pushPromptToComposer}
          />
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {previewPath && (
          <FilePreviewModal
            workspaceId={workspaceId}
            path={previewPath}
            conversationId={conv?.id || ''}
            showDiff={!!previewBaseline}
            baselineContent={previewBaseline}
            onClose={() => { setPreviewPath(null); setPreviewBaseline(null); }}
          />
        )}
        {showModifiedFiles && (
          <ModifiedFilesModal
            files={showModifiedFiles}
            onOpen={(p) => { setShowModifiedFiles(null); onOpenFile(p); }}
            onClose={() => setShowModifiedFiles(null)}
          />
        )}
        {showSummary && (
          <SummaryDetailModal
            content={showSummary} onClose={() => setShowSummary(null)}
          />
        )}
        {deployModalOpen && (
          <DeployModal
            workspaceId={workspaceId}
            busy={deployBusy}
            setBusy={setDeployBusy}
            onClose={() => setDeployModalOpen(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const ModifiedFilesModal: React.FC<{
  files: string[]; onOpen: (p: string) => void; onClose: () => void;
}> = ({ files, onOpen, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-3 chat-safe-bottom"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.97 }} animate={{ scale: 1 }}
      className="w-full max-w-md nx-glass-strong rounded-2xl overflow-hidden flex flex-col max-h-[80vh]"
      onClick={(e) => e.stopPropagation()}
      data-testid="chat-modified-files-modal"
    >
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <div className="inline-flex items-center gap-2 text-sm text-zinc-100">
          <Files className="w-4 h-4 text-violet-300" />
          {files.length} archivo{files.length > 1 ? 's' : ''} modificado{files.length > 1 ? 's' : ''}
        </div>
        <button onClick={onClose} className="text-zinc-400 p-1"><X className="w-4 h-4" /></button>
      </div>
      <div className="flex-1 overflow-auto p-2">
        {files.map((f, i) => (
          <button key={i} onClick={() => onOpen(f)}
                  className="w-full text-left px-3 py-2 rounded-lg hover:bg-white/[0.04] font-mono text-xs text-zinc-200">
            {f}
          </button>
        ))}
      </div>
    </motion.div>
  </motion.div>
);

const SummaryDetailModal: React.FC<{ content: string; onClose: () => void }> = ({ content, onClose }) => (
  <motion.div
    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-3 chat-safe-bottom"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.97 }} animate={{ scale: 1 }}
      className="w-full max-w-2xl nx-glass-strong rounded-2xl overflow-hidden flex flex-col max-h-[85vh]"
      onClick={(e) => e.stopPropagation()}
      data-testid="chat-summary-detail-modal"
    >
      <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
        <div className="text-sm text-zinc-100">Reporte completo</div>
        <button onClick={onClose} className="text-zinc-400 p-1"><X className="w-4 h-4" /></button>
      </div>
      <pre className="flex-1 overflow-auto p-3 font-mono text-xs text-zinc-300 whitespace-pre-wrap break-words">
        {content}
      </pre>
    </motion.div>
  </motion.div>
);

export default WorkspaceChatPanel;

// ── DeployModal ─────────────────────────────────────────────────────────
// One-click deploy of the workspace to GitHub. If the workspace has
// `config.git_repo` set, we use it as default; otherwise the user pastes
// `owner/repo` inline. Reuses `/api/workspaces/{id}/export/github`.
const DeployModal: React.FC<{
  workspaceId: string;
  busy: boolean;
  setBusy: (b: boolean) => void;
  onClose: () => void;
}> = ({ workspaceId, busy, setBusy, onClose }) => {
  const [repo, setRepo] = useState<string>('');
  const [branch, setBranch] = useState<string>('main');
  const [commit, setCommit] = useState<string>(
    'Deploy from Nexus — automated engineering result',
  );
  const [createRepo, setCreateRepo] = useState(true);
  const [privateRepo, setPrivateRepo] = useState(false);
  const [tokenMissing, setTokenMissing] = useState<boolean | null>(null);
  const [result, setResult] = useState<null | {
    ok: boolean; message: string; url?: string; error?: string;
  }>(null);

  // Prefill from workspace.config.git_repo if available + probe token
  useEffect(() => {
    (async () => {
      try {
        const ws = await apiService.getWorkspace(workspaceId);
        const gr = (ws as any)?.git_repo || '';
        if (gr && /\//.test(gr)) {
          const m = gr.match(/github\.com[/:]([^/]+\/[^/.]+)/);
          setRepo(m ? m[1] : gr.replace(/^https?:\/\/[^/]+\//, '').replace(/\.git$/, ''));
        }
        const br = (ws as any)?.git_branch;
        if (br) setBranch(br);
      } catch { /* fine — user pastes manually */ }
      // Proactive token probe — surface the "connect GitHub" CTA before
      // the user submits and hits a 400.
      try {
        const s = await apiService.getGithubIntegration();
        setTokenMissing(!s?.has_token || !!s?.invalid_since);
      } catch {
        setTokenMissing(true);
      }
    })();
  }, [workspaceId]);

  const doDeploy = async () => {
    const clean = repo.trim().replace(/\.git$/, '').replace(/^https?:\/\/[^/]+\//, '');
    if (!clean || clean.split('/').length !== 2) {
      toast.error('Formato: owner/repo (por ejemplo mi-org/mi-app)');
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const resp = await apiService.deployToGithub(workspaceId, {
        repo: clean,
        branch: branch.trim() || 'main',
        commit_message: commit.trim() || 'Deploy from Nexus',
        create_repo: createRepo,
        private: privateRepo,
      });
      if (resp.ok) {
        setResult({
          ok: true,
          message: `${resp.pushed_files} archivo(s) publicados a ${resp.repo}@${resp.branch}`,
          url: resp.commit_url || undefined,
        });
        toast.success('Deploy completado');
      } else {
        const errParts = [resp.error || 'unknown'];
        if (resp.traceback) errParts.push('\n\n' + resp.traceback);
        setResult({ ok: false, message: 'Deploy falló', error: errParts.join('') });
        toast.error('Deploy falló — revisa los detalles');
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || 'error desconocido';
      const missingToken = typeof detail === 'string'
        && /no github token stored|integrations\/github/i.test(detail);
      if (missingToken) {
        // Categorical UX fix: don't leave the user stranded with a
        // technical error — take them straight to the Integrations
        // page so they can paste a PAT and come back.
        toast.error('Necesitas conectar un token de GitHub primero');
        setResult({
          ok: false, message: 'Falta el token de GitHub',
          error: 'Abre Integraciones para conectar tu Personal Access Token y vuelve aquí.',
        });
      } else {
        setResult({ ok: false, message: 'Deploy falló', error: String(detail) });
        toast.error('Deploy falló');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm grid place-items-center p-3 chat-safe-bottom"
      onClick={onClose}
      data-testid="chat-deploy-modal-overlay"
    >
      <motion.div
        initial={{ scale: 0.96, y: 8 }} animate={{ scale: 1, y: 0 }}
        className="w-full max-w-md nx-glass-strong rounded-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="chat-deploy-modal"
      >
        <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
          <div className="inline-flex items-center gap-2 text-sm text-zinc-100">
            <Sparkles className="w-4 h-4 text-emerald-300" />
            Deploy a GitHub
          </div>
          <button onClick={onClose} className="text-zinc-400 p-1"
                  data-testid="chat-deploy-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {tokenMissing === true && (
            <div
              className="rounded-lg border border-amber-500/30 bg-amber-950/20 text-amber-100 px-3 py-2 flex items-start justify-between gap-3"
              data-testid="chat-deploy-token-missing"
            >
              <div className="text-xs leading-relaxed">
                <div className="font-medium">Falta conectar GitHub</div>
                <div className="opacity-80">
                  Configura tu Personal Access Token una vez y todos tus workspaces
                  podrán hacer Push a GitHub.
                </div>
              </div>
              <a
                href="/integrations" target="_blank" rel="noopener noreferrer"
                data-testid="chat-deploy-connect-github-link"
                className="text-[10px] px-2.5 py-1 rounded-full bg-amber-500/20 hover:bg-amber-500/30 border border-amber-400/30 text-amber-100 whitespace-nowrap"
              >
                Configurar
              </a>
            </div>
          )}
          <label className="block">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">
              Repositorio (owner/repo)
            </div>
            <input
              type="text" value={repo} onChange={(e) => setRepo(e.target.value)}
              placeholder="mi-org/mi-app" autoFocus
              data-testid="chat-deploy-repo-input"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 font-mono focus:outline-none focus:border-violet-400/40"
            />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Branch</div>
              <input
                type="text" value={branch} onChange={(e) => setBranch(e.target.value)}
                data-testid="chat-deploy-branch-input"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 font-mono focus:outline-none focus:border-violet-400/40"
              />
            </label>
            <div className="flex items-end gap-3 text-[11px] text-zinc-300">
              <label className="inline-flex items-center gap-1.5">
                <input type="checkbox" checked={createRepo}
                       onChange={(e) => setCreateRepo(e.target.checked)}
                       data-testid="chat-deploy-createrepo-checkbox" />
                Crear si no existe
              </label>
              <label className="inline-flex items-center gap-1.5">
                <input type="checkbox" checked={privateRepo}
                       onChange={(e) => setPrivateRepo(e.target.checked)}
                       data-testid="chat-deploy-private-checkbox" />
                Privado
              </label>
            </div>
          </div>
          <label className="block">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Commit message</div>
            <input
              type="text" value={commit} onChange={(e) => setCommit(e.target.value)}
              data-testid="chat-deploy-commit-input"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-violet-400/40"
            />
          </label>
          {result && (
            <div
              className={[
                'text-xs rounded-lg px-3 py-2 border',
                result.ok
                  ? 'bg-emerald-500/10 border-emerald-400/25 text-emerald-100'
                  : 'bg-rose-500/10 border-rose-400/25 text-rose-100',
              ].join(' ')}
              data-testid="chat-deploy-result"
            >
              <div className="font-medium">{result.message}</div>
              {result.error && (
                <div className="mt-1 opacity-80 font-mono text-[10px] break-words whitespace-pre-wrap max-h-40 overflow-y-auto">{result.error}</div>
              )}
              {result.url && (
                <a href={result.url} target="_blank" rel="noopener noreferrer"
                   className="mt-1 inline-block underline">
                  Ver commit en GitHub
                </a>
              )}
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-white/10 flex items-center justify-between gap-2">
          <div className="text-[10px] text-zinc-500">
            Requiere un{' '}
            <a href="/integrations" target="_blank" rel="noopener noreferrer"
               className="underline hover:text-zinc-300">
              GitHub token en Integraciones
            </a>.
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} disabled={busy}
                    className="text-xs px-3 py-1.5 rounded-full bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 text-zinc-100 disabled:opacity-40"
                    data-testid="chat-deploy-cancel-btn">
              Cerrar
            </button>
            <button onClick={doDeploy} disabled={busy}
                    className="text-xs px-3 py-1.5 rounded-full bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-400/30 text-emerald-100 disabled:opacity-40 inline-flex items-center gap-1.5"
                    data-testid="chat-deploy-submit-btn">
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {busy ? 'Publicando…' : 'Deploy'}
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
